// Library methods
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { useTheme, Theme } from "@mui/material/styles";
import { DownloadIcon, EditIcon, TrashIcon } from "src/assets/icons";
import EditEMRHubModal from "../EditEMRHubModal/EditEMRHubModal";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import HubUpdateChip from "../HubUpdateChip/HubUpdateChip";
import { useHubUpdate } from "../HubUpdateChip/useHubUpdate";

// Types
import { EMRHubType } from "src/services/emr/types";

// Hooks
import { useDeleteEMRHub } from "src/services/emr/hooks/emr-hubs/useDeleteEMRHub";
import { useDownloadEMRInstaller } from "src/services/emr/hooks/emr-hubs/useDownloadEMRInstaller";

// Styles
import {
  getSurfaceTokens,
  getRequiredErrorColor,
  ModalCustomSurfaceLink,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

const getCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

type HubCardType = {
  emrHub: EMRHubType;
};

const HubField = ({
  label,
  value,
  color,
  onClick,
}: {
  label: string;
  value: string | number;
  color?: string;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  return (
    <Box
      display="flex"
      flexDirection="column"
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        border: 0,
        font: "inherit",
        textAlign: "left",
        color: "inherit",
        cursor: onClick ? "pointer" : "default",
        flexBasis: "calc(50% - 4px)",
        minWidth: 0,
        padding: "8px",
        gap: "8px",
        borderRadius: "4px",
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
      }}
    >
      <Typography fontSize="12px">{label}</Typography>
      <Typography
        fontSize="14px"
        fontWeight={600}
        color={color ?? onSurface}
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const HubCard = ({ emrHub }: HubCardType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const [openEditHubModal, setOpenEditHubModal] = useState(false);
  const [openDeleteHubModal, setOpenDeleteHubModal] = useState(false);

  const { mutate: deleteHub, isPending: isDeletingHub } = useDeleteEMRHub(
    clinicInfo?.id
  );

  const { mutate: downloadInstaller, isPending: isDownloadingInstaller } =
    useDownloadEMRInstaller(clinicInfo?.id);

  const { canDownload, isOutdated, latestVersion } = useHubUpdate(emrHub);

  const problemCount = emrHub?.problemCount ?? 0;

  const statusColor = useMemo(() => {
    if (emrHub?.status === "Revoked") {
      return getRequiredErrorColor(theme);
    } else if (emrHub?.status === "Pending" || problemCount > 0) {
      return "#E2772E";
    }

    return "#22BE39";
  }, [emrHub?.status, problemCount, theme]);

  const connectionsPath = `/data-hub/${emrHub.hubId}/connections`;

  return (
    <Box
      display="flex"
      sx={{
        border:
          theme.palette.mode === "dark"
            ? "1px solid #5B6D7A"
            : "1px solid rgba(0, 0, 0, 0.2)",
        backgroundColor: getCardBackground(theme),
        borderRadius: "8px",
        width: "100%",
      }}
    >
      <Box display="flex" flexDirection="column" width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="8px"
          sx={{
            p: "8px",
            borderBottom:
              theme.palette.mode === "dark"
                ? "1px solid #5B6D7A"
                : "1px solid rgba(0, 0, 0, 0.2)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            minWidth={0}
            flex={1}
          >
            <Typography
              sx={{
                flexShrink: 0,
                color: statusColor,
                fontSize: "14px",
                fontWeight: 600,
                border: `1px solid ${statusColor}`,
                borderRadius: "4px",
                p: "0px 4px",
              }}
            >
              {t(emrHub.status)}
              {problemCount > 0 &&
                emrHub.status !== "Revoked" &&
                ` · ${t("{{count}} problem", { count: problemCount })}`}
            </Typography>
            <Typography
              fontSize="14px"
              fontWeight={600}
              color={onSurface}
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {emrHub.alias}
            </Typography>
          </Box>

          <Typography fontSize="12px" color={onSurface} sx={{ flexShrink: 0 }}>
            {t("Created")}: {emrHub?.createdAt?.slice(0, 10)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "8px",
            gap: "8px",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              width: "100%",
              padding: "8px",
              gap: "8px",
              borderRadius: "4px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
            }}
          >
            <Typography fontSize="12px">{t("Associated Clinics")}</Typography>

            <Typography
              fontSize="14px"
              fontWeight={600}
              color={onSurface}
              sx={{ wordBreak: "break-word" }}
            >
              {emrHub?.associatedClinics
                ?.map((clinic) => clinic.name)
                ?.join(", ")}
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            sx={{
              width: "100%",
              padding: "8px",
              gap: "8px",
              borderRadius: "4px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
            }}
          >
            <Typography fontSize="12px">
              {t("Network Interfaces")}{" "}
              <span style={{ fontWeight: 600 }}>(MAC : IPv4)</span>
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {emrHub?.networkInterfaces &&
              emrHub?.networkInterfaces?.length > 0 ? (
                emrHub?.networkInterfaces?.map((network) => (
                  <Box key={network.mac} sx={{ display: "flex" }}>
                    <Typography fontWeight={600} fontSize="14px">
                      {network?.mac && network?.mac?.length > 0
                        ? network?.mac
                        : "N/A"}{" "}
                      {" : "}{" "}
                      {network?.ipv4 && network?.ipv4?.length > 0
                        ? network.ipv4?.join(", ")
                        : "N/A"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography fontWeight={600} fontSize="14px">
                  N/A
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: "8px" }}>
            <HubField
              label={t("Needs Attention")}
              value={emrHub?.needsAttentionCount ?? 0}
            />
            <HubField label={t("Queue")} value={emrHub?.queueDepth ?? 0} />
            <HubField
              label={t("Problems")}
              value={problemCount}
              color={problemCount ? getRequiredErrorColor(theme) : undefined}
              onClick={
                problemCount ? () => navigate(connectionsPath) : undefined
              }
            />
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap="8px"
          >
            <HubUpdateChip emrHub={emrHub} />
            <Typography textAlign="right" fontSize="12px">
              {t("App Version")}: {emrHub?.appVersion ?? "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          padding="8px"
          sx={{
            borderTop:
              theme.palette.mode === "dark"
                ? "1px solid #5B6D7A"
                : "1px solid rgba(0, 0, 0, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "40px",
              gap: "16px",
            }}
          >
            <Button
              sx={ModalCustomSurfaceLink}
              onClick={() => navigate(connectionsPath)}
            >
              {t("Connections")}
            </Button>
            <Button
              sx={{
                ...ModalCustomSurfaceLink(theme),
                minWidth: "fit-content",
              }}
              onClick={() => navigate(`/data-hub/${emrHub.hubId}/jobs`)}
            >
              {t("Jobs")}
            </Button>
          </Box>
          <Box sx={{ display: "flex" }}>
            {canDownload && (
              <Tooltip
                title={
                  isOutdated
                    ? t("Update to {{version}}", { version: latestVersion })
                    : t("Download EMR Installer")
                }
              >
                <IconButton
                  onClick={() =>
                    downloadInstaller({
                      hubId: emrHub.hubId,
                      alias: emrHub.alias,
                    })
                  }
                  disabled={isDownloadingInstaller}
                  aria-label={
                    isOutdated
                      ? t("Update to {{version}}", { version: latestVersion })
                      : t("Download EMR Installer")
                  }
                  sx={{
                    color: isOutdated ? "#E2772E" : onSurface,
                    ...noHoverBackgroundSx,
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}

            {emrHub?.status !== "Revoked" && (
              <Tooltip title={t("Edit EMR Hub")}>
                <IconButton
                  onClick={() => setOpenEditHubModal(true)}
                  sx={{
                    color: onSurface,
                    ...noHoverBackgroundSx,
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}

            {emrHub?.status !== "Revoked" && (
              <Tooltip title={t("Delete EMR Hub")}>
                <IconButton
                  onClick={() => setOpenDeleteHubModal(true)}
                  sx={{
                    color: getRequiredErrorColor(theme),
                    ...noHoverBackgroundSx,
                  }}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <EditEMRHubModal
        open={openEditHubModal}
        clinicId={clinicInfo?.id}
        hubId={emrHub.hubId}
        currentAlias={emrHub.alias}
        homeClinicId={emrHub.homeClinicId}
        currentAssociatedClinics={emrHub.associatedClinics}
        onClose={() => setOpenEditHubModal(false)}
      />

      <SharedModal
        open={openDeleteHubModal}
        title={t("Delete EMR Hub")}
        modalDescription={t(
          "Are you sure you want to delete this EMR hub? This action cannot be undone."
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        isDisabledButton={isDeletingHub}
        onClose={() => setOpenDeleteHubModal(false)}
        onConfirm={() => {
          deleteHub(emrHub.hubId, {
            onSuccess: () => setOpenDeleteHubModal(false),
          });
        }}
      />
    </Box>
  );
};

export default HubCard;
